/* import __COLOCATED_TEMPLATE__ from './company-profile.hbs'; */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import clsx from 'clsx';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { mapResponseToModel } from 'sh-www/utils/store';

const CLASS_NAME = 'SH-c-company-profile';

const SECTIONS = {
  PROFILE: 'profile',
  POSTINGS: 'postings',
  MEMBERS: 'members',
  CODES: 'codes',
  BRANDS: 'brands',
  BILLING: 'billing',
  ASSETS_LEGAL: 'assets-legal',
  BLOCKED: 'blocked',
};

const ADMIN_SECTIONS = {
  ACCOUNT: 'account',
  FEES: 'fees',
  CREDITS: 'credits',
  NOTES: 'notes',
};

const SECTION_TITLES = {
  ACCOUNT: 'Account',
  PROFILE: 'Profile',
  POSTINGS: 'Postings',
  MEMBERS: 'Members',
  CODES: 'Cost & Story Codes',
  BRANDS: 'Managed Brands',
  BILLING: 'Billing',
  ASSETS_LEGAL: 'Assets & Legal',
  BLOCKED: 'Blocked',
  FEES: 'Tiers & Fees',
  CREDITS: 'Credits',
  NOTES: 'Notes',
};

export const COMPANY_PROFILE_COMPONENT = {
  SECTIONS,
  ADMIN_SECTIONS,
  SECTION_TITLES,
};

export default class CompanyProfile extends Component {
  @service('state-managers/application') applicationStateManager;
  @service('commands/assignment-commands') assignmentCommandService;
  @service('commands/company-commands') companyCommandService;
  @service('commands/image-commands') imageCommandService;
  @service('error-tracker') errorTracker;

  @service session;
  @service api;

  /** @type {Boolean} Whether a new company admin note is being made. */
  @tracked isAddingNewNote = false;

  get classNames() {
    return clsx(
      CLASS_NAME,
      this.args.class,
    );
  }

  get activeSection() {
    return this.args.section || SECTIONS.PROFILE;
  }

  /** @type {Ember.A<Object>} The items for the subnav menu. */
  get _navItems() {
    const navItems = [];

    Object.keys(COMPANY_PROFILE_COMPONENT.SECTIONS).forEach((section) => {
      if (this.canViewSection(SECTIONS[section])) {
        navItems.push({
          title: SECTION_TITLES[section],
          isActive: () => this.activeSection === SECTIONS[section],
        });
      }
    });
    return navItems;
  }

  /** @type {Ember.A<Object>} The items for the secondary subnav menu. */
  get adminNavItems() {
    const navItems = [];

    if (!this.hasAdminPermission) {
      return navItems;
    }

    Object.keys(COMPANY_PROFILE_COMPONENT.ADMIN_SECTIONS).forEach((section) => {
      navItems.push({
        title: SECTION_TITLES[section],
        isActive: () => this.activeSection === ADMIN_SECTIONS[section],
      });
    });
    return navItems;
  }

  canViewSection(section) {
    if (section === COMPANY_PROFILE_COMPONENT.SECTIONS.BRANDS) {
      return this.args.company.features.brands;
    }

    if (this.hasAdminPermission) {
      return true;
    }

    switch (section) {
      case COMPANY_PROFILE_COMPONENT.SECTIONS.PROFILE:
        return true;

      case COMPANY_PROFILE_COMPONENT.SECTIONS.POSTINGS:
        return !this.session.currentUser.isBuyer;

      case COMPANY_PROFILE_COMPONENT.SECTIONS.MEMBERS:
      case COMPANY_PROFILE_COMPONENT.SECTIONS.CODES:
      case COMPANY_PROFILE_COMPONENT.SECTIONS.BILLING:
      case COMPANY_PROFILE_COMPONENT.SECTIONS.ASSETS_LEGAL:
      case COMPANY_PROFILE_COMPONENT.SECTIONS.BLOCKED:
        return this.hasCompanyMemberPermission;

      default:
        return false;
    }
  }

  /** @type {Boolean} Whether the current user is an admin */
  get hasAdminPermission() {
    return this.session.currentUser.isAdmin;
  }

  /** @type {Boolean} Whether the current user can edit the company. */
  get hasCompanyMemberPermission() {
    return this.hasAdminPermission ||
      (this.session.currentUser.company?.id === this.args.company.id &&
        this.session.currentUser.isCompanyEditor);
  }

  /** @type {Boolean} Whether the current user is an admin or admin of the company. */
  get hasAdminOrCompanyAdminPermission() {
    return this.hasAdminPermission ||
      (this.session.currentUser.company?.id === this.args.company.id &&
        this.session.currentUser.isCompanyEditor &&
        this.session.currentUser.isCompanyAdmin);
  }

  @action
  async uploadCompanyImage(file) {
    try {
      return await this.imageCommandService.createDraftImage(file);
    } catch (e) {
      this.errorTracker.error('Failed to upload company image', this, e);
      throw e;
    }
  }

  @action
  async saveCompanySettings(jsonData) {
    try {
      return mapResponseToModel(
        this,
        'company',
        await this.api.put(`/companies/${this.args.company.id}`, {
          jsonData,
          params: {
            include: 'location',
          },
        }),
      );
    } catch (e) {
      this.errorTracker.error('Failed to save company settings', this, e);
      throw e;
    }
  }

  @action
  deleteAsset(asset) {
    this.companyCommandService
      .deleteAsset(this.args.company, asset)
      .then(() => this.applicationStateManager.addToast('File deleted'));
  }

  /**
   * Change whether a new note is currently being added
   *
   * @param {Boolean} createNew
   */
  @action
  onToggleAddNewNote(createNew) {
    this.isAddingNewNote = createNew;
  }

  @action
  onNavItemClicked(sectionTitle) {
    const section = Object.keys(SECTION_TITLES).find((section) => {
      return sectionTitle === SECTION_TITLES[section];
    })

    this.args.changeSection(SECTIONS[section] || ADMIN_SECTIONS[section]);
  }
}
